'use client'

import { useState, useEffect } from 'react'
import { Icon } from '@/icons/Icon'
import { getThumbnail, normalizeThumbnailRef } from '@/components/content/videos/helpers'
import VideoThumbnailImage from '@/components/content/videos/VideoThumbnailImage'
import YoutubePreconnect from '@/components/content/videos/YoutubePreconnect'
import { useSegmentPlayer } from '@/components/content/video-segment/SegmentPlayerContext'
import { queryPayload } from '@/lib/payloadClient'
import { cn, scrollToSection } from '@/lib/utils'
import { getYoutubeIdFromInput, type YoutubeThumbnailQuality } from '@/lib/youtube'
import type { Video } from '@/payload-types'

const FALLBACK_VIDEO_ID = 2037

type ResolvedVideo = Pick<
  Video,
  'id' | 'thumbnail' | 'name' | 'url' | 'duration' | 'youtubeId' | 'thumbnailOverride'
>

type VideoModalProps = {
  label?: string
  video?: ResolvedVideo
  theme?: 'light' | 'dark'
  className?: string
  // Bump to 'medium' on pages where a full-size VideoPlayer renders the same
  // video — the browser dedupes the URL and avoids a second YouTube CDN fetch.
  thumbnailQuality?: YoutubeThumbnailQuality
}

export default function VideoModal({
  label = 'Watch how Efficient App works',
  video,
  theme = 'light',
  className,
  thumbnailQuality = 'small',
}: VideoModalProps) {
  const [resolvedVideo, setResolvedVideo] = useState<ResolvedVideo | null>(video ?? null)
  const { play } = useSegmentPlayer()

  useEffect(() => {
    let cancelled = false

    if (video) {
      setResolvedVideo(video)
      return
    }

    async function fetchAndSetResolvedVideo() {
      try {
        let { docs: videoDocs } = await queryPayload({
          collection: 'video',
          where: { isExplainerVideo: { equals: true } },
          select: {
            name: true,
            url: true,
            duration: true,
            youtubeId: true,
            thumbnail: true,
            thumbnailOverride: true,
          },
          populate: { media: { url: true, alt: true } },
          limit: 1,
          depth: 1,
        })

        if (!videoDocs || videoDocs.length === 0) {
          const { docs: fallbackDocs } = await queryPayload({
            collection: 'video',
            where: { id: { equals: FALLBACK_VIDEO_ID } },
            select: {
              name: true,
              url: true,
              duration: true,
              youtubeId: true,
              thumbnail: true,
              thumbnailOverride: true,
            },
            populate: { media: { url: true, alt: true } },
            limit: 1,
            depth: 1,
          })
          videoDocs = fallbackDocs
        }

        if (cancelled) return
        const [fetchedVideo] = videoDocs
        setResolvedVideo((fetchedVideo as ResolvedVideo) ?? null)
      } catch {
        if (cancelled) return
        setResolvedVideo(null)
      }
    }

    void fetchAndSetResolvedVideo()

    return () => {
      cancelled = true
    }
  }, [video])

  // Some Video docs (legacy/migrated) have a valid YouTube url but a blank
  // youtubeId, since the field isn't required and only backfills on save
  // (src/collections/Video/hooks.ts). Fall back to parsing url so playback
  // and clickability agree with getThumbnail's own fallback.
  const playableYoutubeId = resolvedVideo
    ? (getYoutubeIdFromInput(resolvedVideo.youtubeId) ?? getYoutubeIdFromInput(resolvedVideo.url))
    : null

  // No on-page anchor to register (see SegmentPlayRequest.playerKey), so this
  // always hands off to SegmentPlayer's floating popout - same as
  // VideoSegmentChip/VideoAppChip, which play the same way for the same reason.
  function playResolvedVideo(): void {
    if (!resolvedVideo || !playableYoutubeId) return
    play({
      youtubeId: playableYoutubeId,
      startSeconds: 0,
      segmentName: resolvedVideo.name,
      segmentId: resolvedVideo.id,
      videoName: resolvedVideo.name,
      videoId: resolvedVideo.id,
      thumbnail: normalizeThumbnailRef(resolvedVideo.thumbnail),
      thumbnailOverride: normalizeThumbnailRef(resolvedVideo.thumbnailOverride),
    })
  }

  const thumbnail = resolvedVideo ? getThumbnail(resolvedVideo) : null
  return (
    <>
      {thumbnail?.source === 'youtube' && <YoutubePreconnect />}
      <div
        onClick={
          video
            ? () =>
                scrollToSection({
                  id: 'video',
                  offset: { mobile: 200, desktop: 100 },
                  animate: true,
                  hideHash: true,
                })
            : playableYoutubeId
              ? playResolvedVideo
              : undefined
        }
        className={cn(
          'group relative z-10 flex h-[46px] w-fit items-center rounded-xl p-2 transition-colors',
          (video || playableYoutubeId) && 'cursor-pointer',
          theme === 'dark'
            ? 'border border-white/15 bg-white/5 text-gray-100 hover:border-white/25 hover:text-white'
            : 'bg-gray-100 text-black hover:bg-primary-500 hover:text-white',
          className,
        )}
      >
        <div className="relative h-7 w-11 overflow-hidden rounded-md bg-black">
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-gradient-to-t from-black/75 to-transparent to-75% text-white">
            <Icon name="media-play" />
          </div>
          {thumbnail && (
            <VideoThumbnailImage
              key={thumbnail.url}
              thumbnail={thumbnail}
              quality={thumbnailQuality}
              alt={label}
              fill
              sizes="44px"
              className="relative z-10 opacity-80"
            />
          )}
        </div>
        <p className="pl-3 pr-2 text-14 font-medium transition-colors">{label}</p>
      </div>
    </>
  )
}
